import { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Mail, Save, Send, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SMTPConfigPage() {
  const { api } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [config, setConfig] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'NEXUS',
    use_tls: true,
    configured: false
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/admin/smtp');
      if (response.data.configured) {
        setConfig({ ...response.data, password: '' });
      }
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.host || !config.port || !config.username || !config.from_email) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    // Se não tem password novo e já está configurado, não envia o campo
    if (!config.password && config.configured) {
      toast.error('Digite a senha do SMTP');
      return;
    }

    setSaving(true);
    try {
      await api.post('/admin/smtp', config);
      toast.success('Configurações salvas!');
      setConfig(prev => ({ ...prev, configured: true, password: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Digite um email para teste');
      return;
    }

    setTesting(true);
    try {
      await api.post('/admin/smtp/test', { test_email: testEmail });
      toast.success('Email de teste enviado! Verifique sua caixa de entrada.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar teste');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <Mail className="w-8 h-8 text-primary" />
          Configuração SMTP
        </h1>
        <p className="text-muted-foreground">Configure o servidor de email para verificação em duas etapas</p>
      </div>

      {/* Status */}
      <div className={`flex items-center gap-2 p-3 rounded-lg ${config.configured ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'}`}>
        {config.configured ? (
          <>
            <CheckCircle2 className="w-5 h-5" />
            <span>SMTP configurado e ativo</span>
          </>
        ) : (
          <>
            <AlertCircle className="w-5 h-5" />
            <span>SMTP não configurado - verificação em duas etapas desativada</span>
          </>
        )}
      </div>

      <Card className="p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Servidor SMTP *</Label>
            <Input
              placeholder="smtp.gmail.com"
              value={config.host}
              onChange={(e) => setConfig({ ...config, host: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Porta *</Label>
            <Input
              type="number"
              placeholder="587"
              value={config.port}
              onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value) || 587 })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Usuário SMTP *</Label>
            <Input
              placeholder="seu@email.com"
              value={config.username}
              onChange={(e) => setConfig({ ...config, username: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Senha SMTP *</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder={config.configured ? '••••••••' : 'senha ou app password'}
                value={config.password}
                onChange={(e) => setConfig({ ...config, password: e.target.value })}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Email de Envio *</Label>
            <Input
              placeholder="noreply@seudominio.com"
              value={config.from_email}
              onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Nome do Remetente</Label>
            <Input
              placeholder="NEXUS"
              value={config.from_name}
              onChange={(e) => setConfig({ ...config, from_name: e.target.value })}
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <div>
            <Label>Usar TLS</Label>
            <p className="text-xs text-muted-foreground">Conexão segura (recomendado)</p>
          </div>
          <Switch
            checked={config.use_tls}
            onCheckedChange={(checked) => setConfig({ ...config, use_tls: checked })}
          />
        </div>

        <Button onClick={handleSave} disabled={saving} className="w-full">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Salvar Configurações
            </>
          )}
        </Button>
      </Card>

      {/* Teste de Email */}
      {config.configured && (
        <Card className="p-6 space-y-4">
          <h3 className="font-bold flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            Testar Configuração
          </h3>
          <div className="flex gap-2">
            <Input
              placeholder="email@teste.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleTest} disabled={testing} variant="outline">
              {testing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Teste
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Um código de verificação de teste será enviado para o email informado.
          </p>
        </Card>
      )}

      {/* Dicas */}
      <Card className="p-6 border-dashed">
        <h3 className="font-bold mb-3">💡 Dicas de Configuração</h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li><strong>Gmail:</strong> smtp.gmail.com, porta 587 (TLS) ou 465 (SSL). <span className="text-yellow-500">IMPORTANTE: Use "Senha de App"</span> (Conta Google → Segurança → Verificação em 2 etapas → Senhas de app)</li>
          <li><strong>Outlook/Hotmail:</strong> smtp.office365.com, porta 587</li>
          <li><strong>Zoho:</strong> smtp.zoho.com, porta 587 ou 465</li>
          <li><strong>Hostinger:</strong> smtp.hostinger.com, porta 587 ou 465</li>
          <li><strong>SendGrid:</strong> smtp.sendgrid.net, porta 587, usuário: apikey</li>
        </ul>
        <div className="mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
          <p className="text-sm text-yellow-600 dark:text-yellow-400">
            ⚠️ <strong>Gmail/Google:</strong> Não use sua senha normal! Você precisa criar uma "Senha de App" nas configurações de segurança da sua conta Google.
          </p>
        </div>
      </Card>
    </div>
  );
}
